import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trophy, Gift, Car, Users, PiggyBank, Cake, Copy, Zap, Share2, CreditCard } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gold = "#F2B705";
const fmtPts = (n) => Number(n || 0).toLocaleString();

const Bar = ({ pct }) => (
  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: gold }} />
  </div>
);

export default function ClubDashboard() {
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bday, setBday] = useState("");

  useEffect(() => {
    const m = params.get("m");
    if (m) axios.get(`${API}/drivers-club/dashboard/${m}`).then((r) => setD(r.data)).catch(() => {});
  }, [params]);

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/drivers-club/lookup`, { query: q });
      setD(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed.");
    } finally { setBusy(false); }
  };

  const saveBday = async () => {
    try {
      await axios.patch(`${API}/drivers-club/member/${d.member.id}/birthday`, { birthday: bday });
      toast.success("Birthday saved — you'll get 2,500 birthday points every year, valid 30 days toward a rental!");
      const { data } = await axios.get(`${API}/drivers-club/dashboard/${d.member.id}`);
      setD(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save."); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(d.referrals.code || "");
    toast.success("Referral code copied — share it anywhere!");
  };

  if (!d) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-5" data-testid="club-dashboard-lookup">
        <div className="max-w-sm w-full text-center">
          <p className="text-[11px] tracking-[0.35em] uppercase" style={{ color: gold }}>Drivers Club</p>
          <h1 className="font-display font-black text-4xl uppercase mt-2">Member Dashboard</h1>
          <p className="text-white/50 text-sm mt-3">Enter the email or phone you joined with.</p>
          <form onSubmit={lookup} className="mt-6 space-y-3">
            <input data-testid="club-lookup-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="you@email.com or (314) 555-0123"
              className="w-full bg-white/[0.04] border border-white/15 focus:border-[#F2B705] rounded-lg outline-none px-4 py-3.5 text-white text-base transition-colors" />
            <button data-testid="club-lookup-submit" disabled={busy}
              className="w-full py-3.5 rounded-full bg-[#F2B705] text-black font-extrabold text-sm tracking-[0.2em] uppercase disabled:opacity-60 flex items-center justify-center gap-2">
              {busy ? <Loader2 size={15} className="animate-spin" /> : null} View My Points
            </button>
          </form>
          <Link to="/drivers-club" className="inline-block mt-5 text-white/40 text-xs underline underline-offset-4">Not a member yet? Join free →</Link>
        </div>
      </div>
    );
  }

  const first = (d.member.name || "").split(" ")[0];
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="club-dashboard">
      <header className="border-b border-white/10 bg-[#050505]/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <span className="font-display font-extrabold">AUTO-HIRE <span style={{ color: gold }}>STL</span> <span className="text-white/40 font-normal text-xs ml-2 tracking-[0.25em] uppercase">Drivers Club</span></span>
          <span data-testid="club-tier-badge" className="text-[10px] font-bold tracking-[0.2em] uppercase border border-[#F2B705]/60 text-[#F2B705] rounded-full px-4 py-1.5">{d.member.tier}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-10 space-y-8">
        <div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase">Hey {first}.</h1>
          <p className="text-white/50 text-sm mt-1">Member since {String(d.member.joined_at || "").slice(0, 10)}</p>
        </div>

        {/* Digital membership card */}
        <section data-testid="club-membership-card" className="rounded-2xl overflow-hidden border border-[#F2B705]/50" style={{ background: "linear-gradient(135deg, #111 0%, #050505 60%, #1a1503 100%)" }}>
          <div className="p-6 sm:p-7 flex flex-wrap items-center gap-6">
            <div className="bg-white rounded-xl p-2.5 shrink-0">
              <QRCodeCanvas value={`${window.location.origin}/club/dashboard?m=${d.member.id}`} size={110} level="M" />
            </div>
            <div className="flex-1 min-w-44">
              <p className="text-[10px] tracking-[0.35em] uppercase text-white/45 flex items-center gap-2"><CreditCard size={12} style={{ color: gold }} /> Drivers Club Member Card</p>
              <p className="font-display font-black text-2xl mt-1">{d.member.name}</p>
              <p className="font-mono text-xs tracking-[0.25em] mt-1" style={{ color: gold }} data-testid="club-member-id">ID {String(d.member.id).slice(0, 8).toUpperCase()}</p>
              <div className="flex items-center gap-4 mt-3 text-sm">
                <span className="font-bold uppercase text-[11px] tracking-[0.2em] border border-[#F2B705]/60 rounded-full px-3 py-1" style={{ color: gold }}>{d.member.tier}</span>
                <span className="text-white/70">{fmtPts(d.points.balance)} pts</span>
              </div>
            </div>
            <button data-testid="club-card-share" onClick={() => {
              const url = `${window.location.origin}/club/dashboard?m=${d.member.id}`;
              if (navigator.share) navigator.share({ title: "AutoHire STL Drivers Club", text: "My Drivers Club member card", url }).catch(() => {});
              else { navigator.clipboard.writeText(url); toast.success("Card link copied — save it to your home screen!"); }
            }} className="flex items-center gap-2 border border-white/25 rounded-full px-5 py-2.5 text-xs font-bold hover:bg-white hover:text-black transition-colors">
              <Share2 size={14} /> Share / Save Card
            </button>
          </div>
        </section>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[[Trophy, "Points Balance", fmtPts(d.points.balance), "club-kpi-points"],
            [Users, "Referrals", d.referrals.count, "club-kpi-referrals"],
            [Car, "Rentals", d.rentals.length, "club-kpi-rentals"],
            [PiggyBank, "Total Savings", `$${fmtPts(d.savings)}`, "club-kpi-savings"]].map(([Icon, l, v, tid]) => (
            <div key={l} data-testid={tid} className="border border-white/10 bg-white/[0.02] rounded-xl p-5">
              <Icon size={18} style={{ color: gold }} />
              <p className="font-display font-black text-3xl mt-2">{v}</p>
              <p className="text-white/40 text-[10px] tracking-[0.2em] uppercase mt-1">{l}</p>
            </div>
          ))}
        </div>

        {/* Tier progress */}
        {d.next_tier && (
          <div className="border border-white/10 bg-white/[0.02] rounded-xl p-5" data-testid="club-tier-progress">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-semibold">{d.member.tier} → <span style={{ color: gold }}>{d.next_tier.name}</span></span>
              <span className="text-white/50">{fmtPts(d.points.lifetime)} / {fmtPts(d.next_tier.at)} lifetime pts</span>
            </div>
            <Bar pct={d.next_tier.progress_pct} />
            <p className="text-white/45 text-xs mt-2">Only <span className="font-bold" style={{ color: gold }}>{fmtPts(d.next_tier.needed)} points</span> to reach {d.next_tier.name}!</p>
          </div>
        )}

        {/* Rewards progress */}
        <section>
          <h2 className="font-display font-extrabold text-xl uppercase mb-3 flex items-center gap-2"><Gift size={18} style={{ color: gold }} /> Rewards</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="club-rewards-grid">
            {d.rewards.map((r) => (
              <div key={r.title} className={`border rounded-xl p-4 ${r.affordable ? "border-[#F2B705]/60 bg-[#F2B705]/[0.06]" : "border-white/10 bg-white/[0.02]"}`}>
                <div className="flex justify-between items-center mb-2">
                  <p className="font-bold">{r.title}</p>
                  <span className="text-xs font-black" style={{ color: gold }}>{fmtPts(r.pts)} pts</span>
                </div>
                <Bar pct={r.progress_pct} />
                <p className="text-xs mt-2">{r.affordable
                  ? <span className="font-bold text-emerald-400">✓ Ready to redeem — call or text us to claim it!</span>
                  : <span className="text-white/45">{fmtPts(d.points.balance)} / {fmtPts(r.pts)} — {fmtPts(r.points_needed)} pts to go</span>}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Referral tracker */}
        <section className="border border-[#F2B705]/40 bg-[#F2B705]/[0.05] rounded-xl p-5" data-testid="club-referral-card">
          <h2 className="font-display font-extrabold text-xl uppercase flex items-center gap-2"><Users size={18} style={{ color: gold }} /> Refer & Earn</h2>
          <p className="text-white/70 text-sm mt-2">Every friend who rents with your code earns you <span className="font-bold" style={{ color: gold }}>500 points</span> — and they get $15 off. {d.referrals.count > 0 ? `You've referred ${d.referrals.count} so far.` : "You haven't referred anyone yet — your first one is 500 easy points."}</p>
          {d.referrals.code && (
            <div className="mt-3 flex items-center gap-2">
              <code data-testid="club-referral-code" className="bg-black/40 border border-white/15 rounded-lg px-4 py-2.5 font-black tracking-[0.2em]" style={{ color: gold }}>{d.referrals.code}</code>
              <button data-testid="club-referral-copy" onClick={copyCode} className="flex items-center gap-1.5 border border-white/20 rounded-lg px-4 py-2.5 text-xs font-bold hover:bg-white hover:text-black transition-colors"><Copy size={13} /> Copy</button>
            </div>
          )}
        </section>

        {/* Challenges */}
        {d.challenges.length > 0 && (
          <section data-testid="club-challenges">
            <h2 className="font-display font-extrabold text-xl uppercase mb-3 flex items-center gap-2"><Zap size={18} style={{ color: gold }} /> Bonus Challenges</h2>
            <div className="space-y-2">
              {d.challenges.map((c) => (
                <div key={c.id} className="border border-white/10 bg-white/[0.02] rounded-xl p-4 flex items-center justify-between gap-3">
                  <div><p className="font-bold">{c.title}</p><p className="text-white/50 text-xs">{c.desc}{c.ends ? ` · ends ${c.ends}` : ""}</p></div>
                  {c.bonus_points > 0 && <span className="font-black whitespace-nowrap" style={{ color: gold }}>+{fmtPts(c.bonus_points)} pts</span>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Birthday */}
        <section className="border border-white/10 bg-white/[0.02] rounded-xl p-5" data-testid="club-birthday-card">
          <h2 className="font-display font-extrabold text-xl uppercase flex items-center gap-2"><Cake size={18} style={{ color: gold }} /> Birthday Reward</h2>
          {d.birthday.set ? (
            <p className="text-sm mt-2">{d.birthday.active_reward
              ? <span className="font-bold text-emerald-400">🎂 Your {Number(d.birthday.active_reward.points).toLocaleString()} birthday points are active — use them toward a rental before {(d.birthday.active_reward.expires_at || "").slice(0, 10)}!</span>
              : <span className="text-white/60">Birthday on file ({d.birthday.month_day || d.member.birthday}) — you get {Number(d.birthday.reward_points || 2500).toLocaleString()} points every year on your birthday, valid {d.birthday.expires_days || 30} days toward any rental.</span>}</p>
          ) : (
            <div className="mt-3 flex items-center gap-2">
              <input data-testid="club-birthday-input" placeholder="MM-DD" value={bday} onChange={(e) => setBday(e.target.value)} maxLength={5}
                className="w-28 bg-white/[0.04] border border-white/15 focus:border-[#F2B705] rounded-lg outline-none px-3 py-2.5 text-white text-sm" />
              <button data-testid="club-birthday-save" onClick={saveBday} className="bg-[#F2B705] text-black text-xs font-extrabold px-5 py-2.5 rounded-lg">Unlock My Birthday Reward</button>
            </div>
          )}
        </section>

        {/* Rental history */}
        {/* Points activity ledger */}
        {(d.activity || []).length > 0 && (
          <section data-testid="club-activity" className="border border-white/10 bg-white/[0.02] rounded-xl p-5">
            <h2 className="font-display font-extrabold text-xl uppercase">Points Activity</h2>
            <div className="mt-3 divide-y divide-white/5">
              {d.activity.slice(0, 15).map((e, i) => (
                <div key={i} className="flex items-center justify-between py-2 text-sm gap-3">
                  <span className="text-white/60 min-w-0 truncate">{e.reason}<span className="text-white/25"> · {(e.at || "").slice(0, 10)}</span></span>
                  <span className={`font-bold shrink-0 ${e.points >= 0 ? "text-emerald-400" : "text-red-300"}`}>{e.points >= 0 ? "+" : ""}{Number(e.points).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section data-testid="club-rental-history">
          <h2 className="font-display font-extrabold text-xl uppercase mb-3 flex items-center gap-2"><Car size={18} style={{ color: gold }} /> Rental History</h2>
          {d.rentals.length === 0 ? (
            <p className="text-white/40 text-sm">No rentals yet — your points start with your first one. <Link to="/" className="underline" style={{ color: gold }}>Book a car →</Link></p>
          ) : (
            <div className="space-y-2">
              {d.rentals.map((r, i) => (
                <div key={i} className="border border-white/10 bg-white/[0.02] rounded-xl px-4 py-3 flex items-center justify-between text-sm">
                  <span className="font-semibold">{r.vehicle}</span>
                  <span className="text-white/45 text-xs">{r.pickup_date} → {r.return_date}</span>
                  <span className="text-white/70">${fmtPts(r.total)}</span>
                  <span className="text-[10px] uppercase tracking-wider text-white/40">{r.status}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <footer className="border-t border-white/10 py-8 text-center text-white/30 text-xs">
        Drivers Club by Auto-Hire STL · (314) 936-1837 · <Link to="/drivers-club" className="underline">Program details</Link>
      </footer>
    </div>
  );
}
